import { useApolloClient } from '@apollo/client';
import _ from 'lodash';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useKeyboardNavigation, useSearch } from '@treams/treams-hooks';
import { SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Highlight,
	Input,
	Lazy,
	Modal,
	Paragraph,
	Popover,
	Radio,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

const Result = styled(Flex)<{ first?: boolean; selected?: boolean }>`
	cursor: pointer;

	${(props) => !props.first && `border-top: 1px solid ${tokens.surface.secondary};`}
	${(props) => props.selected && `background: ${tokens.surface.secondary};`}
`;

type Props = {
	incomingUser: {
		field: 'email' | 'username';
		value: string;
		externalReferenceID: string;
	};
	onDismiss: () => void;
	onSuccess: () => void;
};

export default function ConnectorReplaceUserModal({ incomingUser, onDismiss, onSuccess }: Props) {
	const { t } = useTranslation();
	const [query, setQuery] = useState(incomingUser.value || '');
	const [selectedUser, setSelectedUser] = useState<{
		id: string;
		firstName: string;
		lastName: string;
	} | null>(null);
	const [error, attempt, reset] = useErrorBoundary();

	const {
		results,
		loading: isSearchLoading,
		version,
	} = useSearch(query, {
		status: [graphql.UserStatus.Active, graphql.UserStatus.Inactive, graphql.UserStatus.Archived],
	});

	const { onKeyDown, selectedIndex, setSelectedIndex } = useKeyboardNavigation({
		count: results.length,
		key: version,
		onEnter: (index) => {
			setSelectedUser(results[index]);
		},
	});

	const client = useApolloClient();
	const [updateUser, { loading: updating }] = graphql.useUpdateUserMutation();

	const handleReplace = attempt(async () => {
		if (!selectedUser) {
			return;
		}

		reset();

		const userResponse = await client.query<graphql.UserQuery>({
			query: graphql.UserDocument,
			variables: { id: selectedUser.id },
			fetchPolicy: 'network-only',
		});

		const user = userResponse.data?.user;
		if (!user) {
			throw new Error('User not found');
		}

		await updateUser({
			variables: {
				input: {
					id: selectedUser.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					username: user.username,
					locale: user.locale,
					activatedAt: user.activatedAt,
					groupID: user.group.id,
					localDeactivatedAt: user.localDeactivatedAt,
					isExternal: user.isExternal,

					// Connect the selected user to the new external reference ID.
					externalReferenceID: incomingUser.externalReferenceID,
				},
			},
		});

		onSuccess();
		onDismiss();
	});

	const isLoading = isSearchLoading && results.length === 0;
	const isEmpty = !isSearchLoading && results.length === 0;
	const hasResults = results.length > 0;

	return (
		<Popover visible center>
			<Modal width={500}>
				<Modal.Header title={t('settings.connectors.replaceUser.title')} onClose={onDismiss} />
				<Modal.Contents>
					<Box mb={3}>
						<Paragraph>{t('settings.connectors.replaceUser.selectUser')}</Paragraph>
					</Box>

					<Box mb={3}>
						<Input
							placeholder={t('settings.connectors.replaceUser.searchPlaceholder')}
							onChange={setQuery}
							onKeyDown={onKeyDown}
							value={query}
							autoFocus
							icon={<SearchIcon />}
							loading={isSearchLoading}
						/>
					</Box>

					{error && (
						<Box mb={3}>
							<p style={{ color: 'red' }}>{error.message}</p>
						</Box>
					)}

					<Separator />
					<Box overflowY="auto" maxHeight={300}>
						{isLoading && (
							<Flex justifyContent="center" p={3}>
								<Spinner />
							</Flex>
						)}

						{isEmpty && (
							<Box py={3}>
								<Paragraph>{t('settings.connectors.replaceUser.noUsersFound')}</Paragraph>
							</Box>
						)}

						{hasResults &&
							_.chunk(results, 10).map((pageResults, page) => (
								<Lazy key={page} estimatedHeight={pageResults.length * 33 - 1}>
									{pageResults.map((result, i) => {
										const index = page * 10 + i;
										const isSelected = index === selectedIndex;
										return (
											<Fragment key={result.id}>
												<Result
													p={2}
													alignItems="center"
													first={i === 0 && page === 0}
													selected={isSelected || selectedUser?.id === result.id}
													onMouseEnter={() => setSelectedIndex(index)}
													onClick={() => setSelectedUser(result)}
												>
													<Radio
														checked={selectedUser?.id === result.id}
														onChange={() => setSelectedUser(result)}
														mr={2}
													/>
													<Avatar user={result} size={32} />
													<Box ml={2} flexGrow={1} flexShrink={1}>
														<Highlight query={query}>
															{result.firstName} {result.lastName}
														</Highlight>
														{result.email && (
															<Box fontSize={0} color="gray">
																<Highlight query={query}>{result.email}</Highlight>
															</Box>
														)}
													</Box>
												</Result>
											</Fragment>
										);
									})}
								</Lazy>
							))}
					</Box>
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={onDismiss}
					onSave={handleReplace}
					saveTitle={t('settings.connectors.replaceUser.replace')}
					disabled={!selectedUser || updating}
				/>
			</Modal>
		</Popover>
	);
}
